import { useEffect, useState } from 'react';
import { Button, Checkbox, Grid, GridCol, Group, Loader, Select, Table, Tabs, TextInput, Title } from '@mantine/core';
import { MODE } from '.';
import { useForm } from '@mantine/form';
import { IUser, PermissionOption } from '../../../libraries/user/index';
import { Language } from '../../languages/languageHandler';
import { Distribuition, DistribuitionsResponse, Event } from '../../../libraries/distribuition-list';
import ChooseUserModal from '../../helpdesk-app/components/choose-user-modal';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

interface FormProps {
  isNew?: boolean;
  mode: string;
  distribuition: Distribuition;
  submitForm: (data: any) => void;
  deleteDistribuition: (data: any, mode: string) => void;
  lang: Language;
  eventsList?: Event[];
  activeTab: string;
  setActiveTab: (value: string) => void;
  permissionOptions: PermissionOption[];
  togglePermissions: (permissions: PermissionOption) => void;
  className?: string;
}

const DistribuitionForm = ({
  mode,
  distribuition,
  submitForm,
  deleteDistribuition,
  lang,
  eventsList,
  activeTab,
  setActiveTab,
  permissionOptions,
  togglePermissions
}: FormProps) => {
  const [selectedPermissions, setSelectedPermissions] = useState<PermissionOption[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [loading] = useState<boolean>(false);
  const [noResults] = useState<boolean>(false);
  const [message] = useState<DistribuitionsResponse>();
  const [emailInput, setEmailInput] = useState<string>('');

  useEffect(() => {
    if (!distribuition?.id) return;

    form.setValues({
      emails: distribuition.emails || [],
      eventCode: distribuition.eventCode ?? 0,
      eventDescription: distribuition.eventDescription ?? '',
      id: distribuition.id ?? '',
      permissions: distribuition.permissions || [],
      users: distribuition.users || []
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distribuition]);

  useEffect(() => {
    if (selectedPermissions.length === 0) {
      setSelectedPermissions(
        permissionOptions.map(permission => ({
          ...permission,
          selected:
            distribuition.permissions?.some(
              p => p.appId === permission.appId && (p.permissionCode & permission.mask) !== 0
            ) || false
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distribuition, permissionOptions]);

  const getPermissionLabel = (langKey: string, lang: any) => {
    return lang[langKey];
  };

  const toggleSelection = (permission: PermissionOption, index: number) => {
    const updated = [...selectedPermissions];
    updated[index] = {
      ...permission,
      selected: !updated[index].selected
    };
    setSelectedPermissions(updated);
    togglePermissions(permission);
  };

  const form = useForm({
    initialValues: {
      emails: distribuition.emails || [],
      eventCode: distribuition.eventCode ?? 0,
      eventDescription: distribuition.eventDescription ?? '',
      id: distribuition.id ?? '',
      permissions: distribuition.permissions || [],
      users: distribuition.users || []
    }
  });

  const tabConfig = [
    {
      tab: 'permissions',
      label: lang.USER_PERMISSIONS,
      fields: [{ name: 'permissions', span: 12 }]
    },
    {
      tab: 'users',
      label: lang.GLOBAL_USERS,
      fields: [{ name: 'users', span: 12 }]
    },
    {
      tab: 'emails',
      label: 'Emails',
      fields: [{ name: 'emails', span: 12 }]
    }
  ];

  const toggleChooseEntityModal = () => {
    setOpenModal(!openModal);
  };

  const chooseUser = (user: IUser) => {
    form.setValues({
      ...form.values,
      users: [...form.values.users, user]
    });

    toggleChooseEntityModal();
  };

  const removeUser = (name: string) => {
    const updatedUsers = form.values.users.filter(user => user.name !== name);
    form.setValues({
      ...form.values,
      users: updatedUsers
    });
  };

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      return;
    }
    if (form.values.emails.includes(trimmed)) {
      setEmailInput('');
      return;
    }
    form.setValues({
      ...form.values,
      emails: [...form.values.emails, trimmed]
    });
    setEmailInput('');
  };

  const removeEmail = (emailToRemove: string) => {
    const updatedEmails = form.values.emails.filter(e => e !== emailToRemove);
    form.setValues({
      ...form.values,
      emails: updatedEmails
    });
  };

  return (
    <>
      <ChooseUserModal lang={lang} isOpen={openModal} hideBtn={toggleChooseEntityModal} chooseUser={chooseUser} />

      <form onSubmit={form.onSubmit(values => submitForm(values))} className="distribuition-form">
        <Grid>
          <GridCol span={{ base: 12 }}>
            <TextInput
              label="ID"
              placeholder={mode === MODE.NEW ? lang.ENTITY_AUTO_ENTID : 'ID'}
              type="text"
              {...form.getInputProps('id')}
              disabled={mode === MODE.NEW ? true : false}
            />
          </GridCol>

          <GridCol span={{ base: 12 }}>
            <Select
              label={lang.GLOBAL_EVENT}
              placeholder=""
              value={form.values.eventCode.toString()}
              data={
                eventsList?.map(event => ({
                  value: event.eventCode.toString() ?? '',
                  label: `${event.eventCode} - ${event.description}`
                })) || []
              }
              onChange={selectedValue => {
                const selectedEvent = eventsList?.find(event => event.eventCode.toString() === selectedValue);
                if (selectedEvent) {
                  form.setValues({
                    ...form.values,
                    eventCode: selectedEvent.eventCode,
                    eventDescription: selectedEvent.description
                  });
                }
              }}
            />
          </GridCol>

          <Tabs variant="outline" value={activeTab} onChange={value => setActiveTab(value || 'permissions')}>
            <Tabs.List>
              {tabConfig.map(tab => (
                <Tabs.Tab value={tab.tab} key={tab.tab}>
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            <Tabs.Panel value="permissions" pt="sm">
              <Grid>
                <GridCol span={{ base: 12 }}>
                  <Title order={3}>{lang.USER_PERMISSIONS}</Title>
                </GridCol>

                {selectedPermissions.map((permission, index) => (
                  <GridCol span={{ base: 12 }} key={index}>
                    <Checkbox
                      key={permission.appId + '-' + permission.mask}
                      checked={permission.selected}
                      onChange={() => toggleSelection(permission, index)}
                      label={getPermissionLabel(permission.langKey, lang)}
                    />
                  </GridCol>
                ))}
              </Grid>
            </Tabs.Panel>

            <Tabs.Panel value="users" pt="sm">
              <Grid>
                <GridCol span={{ base: 12 }}>
                  <Button radius="md" onClick={toggleChooseEntityModal} leftSection={<Icon.IconSearch stroke={2} />}>
                    {lang.GLOBAL_SEARCH}
                  </Button>
                </GridCol>

                <GridCol span={{ base: 12 }}>
                  <Table.ScrollContainer minWidth={500}>
                    <Table striped highlightOnHover verticalSpacing="sm">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>{lang.PROCESSED_GLASS_ID}</Table.Th>
                          <Table.Th>{lang.GLOBAL_EVENT}</Table.Th>
                          <Table.Th></Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {loading && !message ? (
                          <Table.Tr>
                            <Table.Td colSpan={16}>
                              <Loader color="#016273" />
                            </Table.Td>
                          </Table.Tr>
                        ) : noResults || form.values.users.length === 0 ? (
                          <Table.Tr>
                            <Table.Td colSpan={3} style={{ textAlign: 'center' }}>
                              {lang.GLOBAL_NO_RESULTS}
                            </Table.Td>
                          </Table.Tr>
                        ) : (
                          form.values.users.map((user, index) => (
                            <Table.Tr key={index}>
                              <Table.Td>{user.entid}</Table.Td>
                              <Table.Td>{user.name}</Table.Td>
                              <Table.Td>
                                <Icon.IconX stroke={2} onClick={() => removeUser(user.name || '')} />
                              </Table.Td>
                            </Table.Tr>
                          ))
                        )}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </GridCol>
              </Grid>
            </Tabs.Panel>

            <Tabs.Panel value="emails" pt="sm">
              <Grid>
                <GridCol span={{ base: 9 }}>
                  <TextInput
                    value={emailInput}
                    onChange={e => setEmailInput(e.currentTarget.value)}
                    error={emailInput && !isValidEmail(emailInput) ? 'Invalid email' : null}
                    leftSection={<Icon.IconAt stroke={2} />}
                  />
                </GridCol>

                <GridCol span={{ base: 3 }}>
                  <Button radius="md" fullWidth onClick={addEmail}>
                    {lang.GLOBAL_ADD}
                  </Button>
                </GridCol>

                <GridCol span={{ base: 12 }}>
                  <Table.ScrollContainer minWidth={500}>
                    <Table striped highlightOnHover verticalSpacing="sm">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Email</Table.Th>
                          <Table.Th></Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {form.values.emails.length === 0 ? (
                          <Table.Tr>
                            <Table.Td colSpan={2} style={{ textAlign: 'center' }}>
                              {lang.GLOBAL_NO_RESULTS}
                            </Table.Td>
                          </Table.Tr>
                        ) : (
                          form.values.emails.map((emailObj, index) => (
                            <Table.Tr key={index}>
                              <Table.Td>{emailObj}</Table.Td>
                              <Table.Td>
                                <Icon.IconX onClick={() => removeEmail(emailObj)} />
                              </Table.Td>
                            </Table.Tr>
                          ))
                        )}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </GridCol>
              </Grid>
            </Tabs.Panel>

            <Group className="buttons">
              <Button radius="md" type="submit" className="save">
                {lang.GLOBAL_SAVE_BUTTON}
              </Button>
              <Button radius="md" onClick={() => deleteDistribuition(distribuition, mode)} className="delete">
                {lang.GLOBAL_DELETE}
              </Button>
            </Group>
          </Tabs>
        </Grid>
      </form>
    </>
  );
};

export default DistribuitionForm;
